/**
 * La synthèse d'une affaire — l'écran que la référence ouvre par défaut.
 *
 * **La question qu'un métreur se pose en ouvrant une affaire n'est pas « quels
 * fichiers ai-je ? » mais « puis-je chiffrer ? ».** Le portail répondait à la
 * première : il ouvrait sur les Documents, une liste à parcourir. La référence
 * répond à la seconde, et c'est ce que cet écran fait : la complétude d'abord,
 * ce qui manque ensuite, le travail en cours après.
 *
 * **Rien n'est inventé.** Chaque bloc vient de `completude`, que le portail
 * calcule déjà, ou de la file des traitements. Les blocs de la référence qui
 * n'ont aucune donnée derrière — approbations en attente, réserves bloquantes
 * — sont absents, pas vides.
 */
import type { Completude, Travail } from "../../api/types";
import { Avis } from "../../composants/avis";
import { Chargement } from "../../composants/etats";
import { Etat } from "../../composants/etiquettes";
import { quand } from "../../composants/formats";
import type { Veille } from "../../composants/donnees";

export function Synthese({
  completude,
  etat,
  veille,
  versDocuments,
}: {
  /** **`null` n'est pas `undefined` ici.** `undefined` veut dire « pas encore
   *  reçu » et appelle un squelette ; `null` veut dire « l'API ne le calcule
   *  pas dans cet état » et appelle une explication. Les confondre laissait
   *  tourner un chargement qui n'arrivait jamais. */
  completude?: Completude | null;
  etat: string;
  veille: Veille<Travail>;
  versDocuments: () => void;
}) {
  // **Elle ne va plus chercher la file : la page la lui prête**, et la tient à
  // jour pour tout le monde. Elle l'interrogeait pour son propre compte, à
  // côté du tiroir qui faisait de même — deux relances sur la même adresse
  // pour la même réponse.
  const encours = veille.travaux.filter(
    (t) => t.etat !== "READY" && t.etat !== "CANCELED",
  );

  return (
    <div className="synthese">
      <section>
        <div className="synthese__titre">
          <h2>Complétude des sources</h2>
          <button type="button" className="lien-bouton" onClick={versDocuments}>
            Ouvrir Documents →
          </button>
        </div>

        {completude === undefined ? (
          <Chargement lignes={3} />
        ) : completude === null ? (
          /* **On dit pourquoi il n'y a rien, et par où sortir.** Un écran vide
             sur une affaire archivée se lit comme une panne ; nommer l'état et
             pointer les Paramètres en fait une étape. */
          <div className="note">
            <span>
              {etat === "ARCHIVED"
                ? "Cette affaire est archivée : le relevé des sources n'est "
                  + "plus tenu à jour. Rouvrez-la depuis Paramètres pour le "
                  + "reprendre."
                : "Le relevé des sources n'est tenu que sur une affaire prête. "
                  + `Celle-ci est ${etat.toLowerCase()}.`}
            </span>
          </div>
        ) : (
        <div className="mesures">
          <Mesure
            libelle="CCTP"
            valeur={completude.cctp}
            detail={
              completude.articles_indexes
                ? `${completude.articles_indexes} articles indexés`
                : "aucun article indexé"
            }
            suffisant={completude.cctp > 0 && completude.articles_indexes > 0}
          />
          <Mesure
            libelle="Plans DWG"
            valeur={completude.plans_dwg}
            detail={
              completude.plans_dxf
                ? `${completude.plans_dxf} convertis en DXF`
                : "aucune conversion"
            }
            suffisant={completude.plans_dwg > 0}
          />
          <Mesure
            libelle="Cadre de sortie"
            valeur={completude.modele ? 1 : 0}
            detail={completude.modele ? "classeur en place" : "à fournir"}
            suffisant={completude.modele}
          />
          <Mesure
            libelle="Missions"
            valeur={completude.missions}
            detail={
              completude.signalements
                ? `${completude.signalements} signalement${completude.signalements > 1 ? "s" : ""}`
                : "aucun signalement"
            }
            suffisant={completude.missions > 0}
            alerte={completude.signalements > 0}
          />
        </div>
        )}
      </section>

      {/* **Ce qui manque, et pourquoi.** Le portail le calcule déjà ; le taire
          laissait le métreur deviner ce qui bloque son chiffrage. */}
      {completude != null && completude.manque.length > 0 && (
        <Avis
          ton="attention"
          className="manque"
          // La clé porte ce qui manque : combler une étape rouvre l'avis avec
          // la liste réduite, plutôt que de la laisser fermée sur l'ancienne.
          cle={completude.manque.map((e) => e.titre).join("|")}
        >
          <strong>Avant de pouvoir chiffrer</strong>
          <ul>
            {completude.manque.map((etape) => (
              <li key={etape.titre}>
                <b>{etape.titre}</b> — {etape.raison}
              </li>
            ))}
          </ul>
        </Avis>
      )}

      {completude != null && completude.detenteur_du_classeur && (
        <Avis ton="verrou" cle={completude.detenteur_du_classeur}>
          Classeur verrouillé — {completude.detenteur_du_classeur}
        </Avis>
      )}

      <section>
        <div className="synthese__titre">
          <h2>Traitements en cours</h2>
        </div>
        {veille.charge && veille.travaux.length === 0 ? (
          <Chargement lignes={2} />
        ) : encours.length === 0 ? (
          <div className="note">
            <span>
              Rien en file. Les imports et préparations lancés depuis les
              Documents apparaîtront ici.
            </span>
          </div>
        ) : (
          <div className="liste liste--travaux">
            <div className="liste__entete">
              <span>Genre</span>
              <span>Pièce</span>
              <span>Depuis</span>
              <span>État</span>
            </div>
            {encours.map((travail) => (
              <div key={travail.job_id} className="liste__ligne liste__ligne--fixe">
                <span className="mono">{travail.genre}</span>
                <span className="tronque">{travail.nom ?? "—"}</span>
                <span className="discret mono">{quand(travail.depuis)}</span>
                <Etat genre="travail" valeur={travail.etat} />
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/** Une mesure : le nombre, ce qu'il vaut, et s'il suffit.
 *
 * **Jamais la couleur seule** — le mot « à fournir » dit la même chose que la
 * teinte, et survit à une capture d'écran en noir et blanc. */
function Mesure({
  libelle,
  valeur,
  detail,
  suffisant,
  alerte = false,
}: {
  libelle: string;
  valeur: number;
  detail: string;
  suffisant: boolean;
  alerte?: boolean;
}) {
  const ton = alerte ? " mesure--alerte" : suffisant ? " mesure--pleine" : "";
  return (
    <div className={`mesure${ton}`}>
      <div className="mesure__libelle">{libelle}</div>
      <div className="mesure__valeur">{valeur}</div>
      <div className="mesure__detail">{detail}</div>
    </div>
  );
}
